import React, { useEffect, useMemo, useState } from 'react';
import { Bell, RefreshCw } from 'lucide-react';
import { notificationService } from '../../services/notificationService';
import type { ReminderOffset, ReminderUnit } from '../../types/notification';
import { useLanguage } from '../../features/language/useLanguage';
import { useToast } from '../../components/common/Toast';
import { NotificationToggleCard } from './components/notifications/NotificationToggleCard';
import { ReminderScheduleEditor } from './components/notifications/ReminderScheduleEditor';
import { NotificationDivergenceBanner } from './components/notifications/NotificationDivergenceBanner';
import { NotificationSaveBar } from './components/notifications/NotificationSaveBar';

const UNITS: ReminderUnit[] = ['minutes', 'hours', 'days'];
const MAX = 5;

const remindersEqual = (a: ReminderOffset[], b: ReminderOffset[]) =>
  a.length === b.length &&
  a.every((r, i) => r.value === b[i].value && r.unit === b[i].unit);

export const NotificationSettingsSection: React.FC = () => {
  const { t } = useLanguage();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [reminders, setReminders] = useState<ReminderOffset[]>([]);
  const [divergent, setDivergent] = useState(0);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [baseline, setBaseline] = useState<{
    isEnabled: boolean;
    reminders: ReminderOffset[];
  } | null>(null);

  const dirty = useMemo(() => {
    if (!baseline) return false;
    return (
      baseline.isEnabled !== isEnabled ||
      !remindersEqual(baseline.reminders, reminders)
    );
  }, [baseline, isEnabled, reminders]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await notificationService.getNotificationSettings();
      const nextReminders = r.settings.reminders ?? [];
      setIsEnabled(r.settings.is_enabled);
      setReminders(nextReminders);
      setDivergent(r.divergence?.divergent_work_orders_count ?? 0);
      setBaseline({
        isEnabled: r.settings.is_enabled,
        reminders: nextReminders.map((x) => ({ ...x })),
      });
      setValidationError(null);
    } catch (e) {
      showToast(String(e), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addReminder = (preset?: ReminderOffset) => {
    if (reminders.length >= MAX) return;
    setReminders([...reminders, preset ? { ...preset } : { value: 1, unit: 'hours' }]);
    setValidationError(null);
  };

  const updateReminder = (i: number, patch: Partial<ReminderOffset>) => {
    setReminders(reminders.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
    setValidationError(null);
  };

  const removeReminder = (i: number) => {
    setReminders(reminders.filter((_, idx) => idx !== i));
    setValidationError(null);
  };

  const resetChanges = () => {
    if (!baseline) return;
    setIsEnabled(baseline.isEnabled);
    setReminders(baseline.reminders.map((x) => ({ ...x })));
    setValidationError(null);
  };

  const validate = (): string | null => {
    if (reminders.length > MAX)
      return t(
        'notifications.settings.maxReached',
        undefined,
        `Max ${MAX} reminders`
      );
    for (const r of reminders) {
      if (!Number.isInteger(r.value) || r.value <= 0)
        return t(
          'notifications.settings.invalidValue',
          undefined,
          'Value must be a positive integer'
        );
      if (!UNITS.includes(r.unit))
        return t('notifications.settings.invalidUnit', undefined, 'Invalid unit');
    }
    return null;
  };

  const save = async () => {
    const v = validate();
    if (v) {
      setValidationError(v);
      showToast(v, 'warning');
      return;
    }
    setSaving(true);
    try {
      await notificationService.updateNotificationSettings({
        reminders,
        is_enabled: isEnabled,
      });
      showToast(
        t(
          'notifications.settings.saveSuccess',
          undefined,
          'Notification settings saved'
        ),
        'success'
      );
      await load();
    } catch (e) {
      showToast(
        `${t(
          'notifications.settings.saveError',
          undefined,
          'Failed to save notification settings'
        )}: ${String(e)}`,
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const resync = async () => {
    setResyncing(true);
    try {
      await notificationService.resyncNotifications();
      showToast(
        t(
          'notifications.settings.resyncSuccess',
          undefined,
          'Pending work orders re-synced'
        ),
        'success'
      );
      await load();
    } catch (e) {
      showToast(
        `${t(
          'notifications.settings.resyncError',
          undefined,
          'Failed to re-sync'
        )}: ${String(e)}`,
        'error'
      );
    } finally {
      setResyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
            <RefreshCw className="h-4 w-4 animate-spin" />
            {t('common.loading', undefined, 'Loading…')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-test-id="notification-settings-section"
      className="bg-white shadow sm:rounded-lg overflow-hidden"
    >
      {/* Header */}
      <div className="px-4 py-5 sm:px-6 sm:py-6 border-b border-gray-100">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
            <Bell className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg leading-6 font-semibold text-gray-900">
              {t(
                'notifications.settings.title',
                undefined,
                'Notification Reminders'
              )}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {t(
                'notifications.settings.description',
                undefined,
                'Configure how many times and when reminders are sent before a roast starts.'
              )}
            </p>
          </div>
        </div>
      </div>

      <NotificationToggleCard isEnabled={isEnabled} onToggle={() => setIsEnabled((v) => !v)} />

      <ReminderScheduleEditor
        reminders={reminders}
        max={MAX}
        isEnabled={isEnabled}
        validationError={validationError}
        onAdd={addReminder}
        onUpdate={updateReminder}
        onRemove={removeReminder}
      />

      {divergent > 0 && (
        <NotificationDivergenceBanner count={divergent} resyncing={resyncing} onResync={resync} />
      )}

      <NotificationSaveBar dirty={dirty} saving={saving} onReset={resetChanges} onSave={save} />
    </div>
  );
};
